import { useAppStore } from '../store/useAppStore';
import { useGetProfileSummary } from '../queries/onboardingPolls';
import { useGetPosts } from '../queries/post';
import { useUserSubscription } from '../queries/user';
import { parseISO, isFuture } from 'date-fns';
import { useMemo, useEffect, useCallback } from 'react';
import { compareAsc } from 'date-fns';
import { usePostsPoll } from '../queries/onboardingPolls';
import { useGetSocialAccounts } from '../queries/social';
import { useErrorModalStore } from '../store/useErrorModalStore';
import { getErrorModalMessage } from '../utils/errorMessages';
import { returnTimeUntilExparation } from '../utils/dateHelpers';
import { useNotificationBannerStore } from '../store/useNotificationBannerStore';
// import { getCachedData, setCachedData } from '../utils/cacheUtils';

// const CACHE_KEY = 'instagram-insights';
// const TTL_MS = 3 * 60 * 60 * 1000; // 3 hours in milliseconds

/**
 * Custom hook to manage dashboard data and logic
 * Centralizes all dashboard state and data fetching
 * @returns {Object} Dashboard data, states, and handlers
 */
export const useDashboard = () => {
  const {
    accessKey,
    setSubscription,
    setBusinessName,
    setLogo,
    setSocialAccounts,
    subscription: storedSubscription,
    socialAccounts: storedSocialAccounts,
  } = useAppStore();
  const { openErrorModal } = useErrorModalStore();

  const {
    shouldShowNotification,
    dismissNotification,
    setShouldShowNotification,
    setHasBeenDismissed,
    hasBeenDismissed,
  } = useNotificationBannerStore();

  // Memoize callbacks to prevent infinite loops
  const handleSubscriptionSuccess = (data) => {
    setSubscription(data);
  };

  const handleSubscriptionError = (error) => {
    console.error('Failed to fetch subscription:', error);
  };

  const handlePostsError = (error) => {
    if (error.status >= 500) {
      return openErrorModal(getErrorModalMessage('server_error'), 'server_error');
    }
    if (error?.response?.status === 404) {
      return;
    }

    openErrorModal(getErrorModalMessage('dashboard_posts_error'), 'dashboard_posts_error');
  };

  const handleSocialAccountsError = useCallback(
    (error) => {
      console.error('Failed to fetch social accounts:', error);
      openErrorModal(getErrorModalMessage('social_fetch_error'), 'social_fetch_error');
    },
    [openErrorModal]
  );

  const handleSocialAccountsSuccess = useCallback(
    (data) => {
      setSocialAccounts(data);
    },
    [setSocialAccounts]
  );

  const handleProfileError = (error) => {
    console.error('Failed to fetch profile:', error);
    openErrorModal(getErrorModalMessage('profile_check_error'), 'profile_check_error');
  };

  const {
    data: posts = [],
    isLoading: postsLoading,
    refetch: refetchPosts,
  } = useGetPosts(true, null, handlePostsError);

  const { data: socialAccounts = [], isLoading: accountsLoading } = useGetSocialAccounts(
    !!accessKey && storedSocialAccounts === null, // Only fetch if we have an access key and no social accounts in store
    handleSocialAccountsSuccess,
    handleSocialAccountsError
  );

  const { data: profile, isLoading: profileLoading } = useGetProfileSummary(
    accessKey,
    true,
    null,
    handleProfileError
  );

  const { isLoading: subscriptionLoading } = useUserSubscription(
    !!accessKey && storedSubscription === null, // Only fetch if we have an access key and no subscription in store
    handleSubscriptionSuccess,
    handleSubscriptionError
  );

  useEffect(() => {
    if (profile?.business_name && profile?.logo) {
      setBusinessName(profile?.business_name || '');
      setLogo(profile?.logo || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.business_name, profile?.logo]);

  const hasDraftPosts = useMemo(() => {
    return posts.some((post) => post.status === 'draft');
  }, [posts]);

  const shouldPollPosts = useMemo(() => {
    return !posts.length || hasDraftPosts;
  }, [posts, hasDraftPosts]);
  // eslint-disable-next-line
  const postsPoll = usePostsPoll(shouldPollPosts, 'dashboard');

  const isLoading =
    postsLoading || accountsLoading || profileLoading || storedSubscription === null;

  // Calculate stats
  const stats = {
    totalPosts: posts.length,
    scheduledPosts:
      Array.isArray(posts) && posts?.filter((post) => post.status === 'approved').length,
    pendingPosts: Array.isArray(posts) && posts?.filter((post) => post.status === 'pending').length,
    connectedAccounts: socialAccounts?.length,
    activeAccounts:
      socialAccounts.length > 0
        ? socialAccounts.filter((account) => account.status === 'active').length
        : 0,
  };

  // Get upcoming posts (next 7 days)
  const upcomingPosts = useMemo(
    () =>
      Array.isArray(posts)
        ? posts
            .filter((post) => post.scheduledAt)
            .filter((post) => {
              const postDate = parseISO(post.scheduledAt);
              return isFuture(postDate);
            })
            .sort((a, b) => compareAsc(parseISO(a.scheduledAt), parseISO(b.scheduledAt)))
        : [],
    [posts]
  );

  // Determine how many posts to show based on subscription
  const maxVisiblePosts =
    storedSubscription?.product_name === 'Welcome' || !storedSubscription ? 3 : 4;
  const displayPosts = upcomingPosts.slice(0, maxVisiblePosts);
  const showUpgradeCTA = maxVisiblePosts === 3;
  // add useEffect to check the time every minute and update the notification banner visibility accordingly
  useEffect(() => {
    if (!storedSubscription || storedSubscription?.product_name === 'Welcome') return;

    if (hasBeenDismissed || !storedSubscription?.start_date) return;

    const timeRemaining = returnTimeUntilExparation(storedSubscription?.start_date, 5);

    if (timeRemaining > 0) {
      setShouldShowNotification(true);

      const timeout = setTimeout(() => {
        setShouldShowNotification(false);
      }, timeRemaining);

      return () => clearTimeout(timeout);
    } else {
      setShouldShowNotification(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedSubscription, hasBeenDismissed]);

  const handleCloseNotification = () => {
    dismissNotification();
    setHasBeenDismissed(true);
    refetchPosts();
  };
  const handleTryAgain = () => {
    refetchPosts();
  };

  return {
    posts,
    socialAccounts,
    profile,
    storedSubscription,
    accessKey,
    stats,
    upcomingPosts,
    displayPosts,
    showUpgradeCTA,
    // instagramInsights: finalInstagramInsights,
    // insightsLoading: finalInsightsLoading,
    isLoading,
    postsLoading,
    accountsLoading,
    profileLoading,
    subscriptionLoading,
    shouldShowNotification,
    handleCloseNotification,
    handleTryAgain,
  };
};

export default useDashboard;
